import { useCallback, useEffect, useState } from "react";
import { OptifundDashboardEndPointLogic } from "../../../../../logic/AssetManagement/overview/OptifundDashboard/OptifundDashboardEndPointLogic";

export interface UseOptifundTabDataResult {
  // Forces the effect below to re-run without any of its real dependencies
  // changing - the retry action for a section that failed to load (see
  // Brands.tsx's per-table retry buttons).
  refetch: () => void;
}

// Fetches one BQL query for a tab, blocking until `websiteSharedUid` is
// resolved (per the spec's Interaction section). Re-fetches whenever
// websiteSharedUid/queryId/startDate/endDate/brand/month/year actually
// change - there is no "skip if we already have any data" check, since that
// also blocked legitimate refetches whenever the user picked a new date
// range; the effect's own dependency array is what prevents redundant
// refetching on unrelated re-renders.
export function useOptifundTabData(
  userToken: any,
  websiteSharedUid: string | null,
  queryId: string,
  setData: (data: any) => void,
  setLoading: (isLoading: boolean) => void,
  startDate?: string,
  endDate?: string,
  brand?: string,
  month?: number,
  year?: number,
  setError?: (hasError: boolean) => void
): UseOptifundTabDataResult {
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!websiteSharedUid) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError?.(false);
    OptifundDashboardEndPointLogic.runQuery(
      userToken,
      queryId,
      websiteSharedUid,
      setData,
      startDate,
      endDate,
      brand,
      month,
      year
    )
      .then((success) => {
        if (!cancelled) setError?.(!success);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteSharedUid, queryId, startDate, endDate, brand, month, year, retryToken]);

  const refetch = useCallback(() => setRetryToken((token) => token + 1), []);

  return { refetch };
}
